<div [hidden]="isInitialLoading">
    @if (metricsLoadError) {
        <div class="alert alert-warning py-2 small" role="alert">
            Some dashboard metrics could not be loaded. The due queue may still be available below.
        </div>
    }

    <div class="row g-3 mb-3">
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-danger border-opacity-50">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Jobs due for visit</div>
                    <div class="fs-3 fw-semibold">{{ metrics.jobsDueCount }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-warning border-opacity-50">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Jobs never visited</div>
                    <div class="fs-3 fw-semibold">{{ metrics.jobsNeverVisitedCount }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-success border-opacity-25">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Visits (30 days)</div>
                    <div class="fs-3 fw-semibold">{{ metrics.visitsLast30Days }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div class="card h-100">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Avg days between visits</div>
                    <div class="fs-3 fw-semibold">
                        @if (metrics.averageDaysBetweenVisits != null) {
                            {{ metrics.averageDaysBetweenVisits | number: '1.0-1' }}
                        } @else {
                            —
                        }
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3 mb-3">
        <div class="col-12">
            <div class="card border-danger border-opacity-50 ltv-due-queue-card">
                <div class="card-body pb-2">
                    <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                        <h5 class="card-title mb-0">Jobs due for a leadership visit</h5>
                        <span class="badge rounded-pill text-bg-danger">{{ dueCount }}</span>
                    </div>
                    <p class="small text-muted mb-2">
                        Due when a job reaches 1,200 labor hours or 1 calendar month since the last leadership visit.
                        Only JCDM (department 10) jobs with labor hours in the last two weeks are shown.
                    </p>
                    @if (dueQueueLoadError) {
                        <p class="text-danger small mb-0">Unable to load the due queue. Try refreshing the page.</p>
                    } @else if (jobsDueForVisit.length === 0) {
                        <p class="text-muted small mb-0">No active jobs are currently due for a leadership visit.</p>
                    } @else {
                        <div class="table-responsive ltv-due-queue-scroll">
                            <table class="table table-sm table-hover mb-0">
                                <thead class="ltv-due-queue-thead">
                                    <tr>
                                        <th>Job</th>
                                        <th>Site superintendent</th>
                                        <th>Why due</th>
                                        <th>Last visit</th>
                                        <th>Last visited by</th>
                                        <th class="text-end">Hours since last visit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (row of jobsDueForVisit; track row.jobId) {
                                        <tr [class.ltv-due-row-urgent]="isUrgentDueRow(row)">
                                            <td>
                                                @if (canEdit) {
                                                    <button
                                                        type="button"
                                                        class="btn btn-link btn-sm p-0 align-baseline"
                                                        title="Create leadership visit for this job"
                                                        (click)="addForJob.emit({ jobId: row.jobId, jobName: row.jobName })"
                                                    >
                                                        {{ formatJobDisplay(row.jobId, row.jobName) }}
                                                    </button>
                                                } @else {
                                                    {{ formatJobDisplay(row.jobId, row.jobName) }}
                                                }
                                            </td>
                                            <td>{{ row.siteSuperName || '—' }}</td>
                                            <td class="small">{{ formatDueReasons(row.dueReasons) }}</td>
                                            <td>
                                                @if (row.lastVisitDate) {
                                                    {{ row.lastVisitDate | date: 'mediumDate' }}
                                                    @if (row.daysSinceLastVisit != null) {
                                                        <span class="text-muted small"> ({{ row.daysSinceLastVisit }} days)</span>
                                                    }
                                                } @else {
                                                    <span class="text-muted">—</span>
                                                }
                                            </td>
                                            <td>{{ row.lastVisitedByName || '—' }}</td>
                                            <td class="text-end">{{ formatHours(row.hoursSinceLastVisit) }}</td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3">
        <div class="col-lg-6">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">Visits by leadership member (90 days)</h5>
                    @if (metrics.visitsByMemberLast90Days.length === 0) {
                        <p class="text-muted small mb-0">No visits recorded in the last 90 days.</p>
                    } @else {
                        <div class="d-flex align-items-end gap-1 ltv-bar-wrap">
                            @for (member of metrics.visitsByMemberLast90Days; track member.memberName) {
                                <div class="flex-fill text-center">
                                    <div
                                        class="ltv-bar rounded-top"
                                        [style.height.px]="(member.visitCount / maxVisitCount()) * 120 || 4"
                                        [title]="member.memberName + ': ' + member.visitCount"
                                    ></div>
                                    <div class="small text-muted mt-1 text-truncate" [title]="member.memberName">
                                        {{ member.memberName.split(' ')[0] }}
                                    </div>
                                    <div class="small fw-semibold">{{ member.visitCount }}</div>
                                </div>
                            }
                        </div>
                    }
                </div>
            </div>
        </div>
        <div class="col-lg-6">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">Visit activity</h5>
                    <p class="small text-muted mb-2">
                        Leadership visits recorded in WebApps over the last 30 and 90 days.
                    </p>
                    <div class="d-flex gap-4">
                        <div>
                            <div class="text-muted small">Last 30 days</div>
                            <div class="fs-4 fw-semibold">{{ metrics.visitsLast30Days }}</div>
                        </div>
                        <div>
                            <div class="text-muted small">Last 90 days</div>
                            <div class="fs-4 fw-semibold">{{ metrics.visitsLast90Days }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
